/**
 * ChatBubble — single message bubble in the chat window.
 * Shows message text, image, reply preview, reaction badge, timestamp,
 * and outgoing message status ticks (sent ✓ / seen ✓✓).
 * Long-press (or context menu) triggers message actions.
 */
import { useMemo } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { ExtendedChatMessage } from '@wlb/shared/types/chat';
import { VerticalKebabIcon } from '../../components/ui/icons';
import { MentionText } from '../../components/ui/MentionLink';
import { buildMentionIndex } from '../../utils/mentions';
import { useCurrentProfile } from '../profile/useCurrentProfile';
import './messages.css';

interface ChatBubbleProps {
  message: ExtendedChatMessage;
  isSent: boolean;
  /** True in group threads — received bubbles show the sender's name. */
  isGroup?: boolean;
  /** Omitted where messages have no actions (e.g. the Robi assistant chat). */
  onLongPress?: (message: ExtendedChatMessage, rect: DOMRect) => void;
}

function formatTime(createdAt: Date | number): string {
  const date = typeof createdAt === 'number' ? new Date(createdAt) : createdAt;
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Outgoing-only status ticks: single ✓ for sent, double ✓✓ for seen. */
function StatusTicks({ message }: { message: ExtendedChatMessage }) {
  const { t } = useTranslation();
  if (message.failed) {
    return <span className="chat-bubble__status chat-bubble__status--failed" aria-label={t('chat.statusTicks.failed')}>✕</span>;
  }
  if (message.pending) {
    return <span className="chat-bubble__status chat-bubble__status--pending" aria-label={t('chat.statusTicks.sending')}>⏱</span>;
  }
  if (message.seen) {
    return <span className="chat-bubble__status chat-bubble__status--seen" aria-label={t('chat.statusTicks.seen')}>✓✓</span>;
  }
  if (message.sent) {
    return <span className="chat-bubble__status chat-bubble__status--sent" aria-label={t('chat.statusTicks.sent')}>✓</span>;
  }
  return null;
}

export function ChatBubble({ message, isSent, isGroup = false, onLongPress }: ChatBubbleProps) {
  const { t } = useTranslation();
  const currentProfile = useCurrentProfile();

  // Identities @mentions in this bubble can resolve to: its sender and the
  // signed-in member.
  const mentions = useMemo(
    () =>
      buildMentionIndex([
        {
          userGuid: currentProfile?.userGuid,
          username: currentProfile?.userName,
          firstName: currentProfile?.firstName,
          displayName: currentProfile?.name,
        },
        {
          userGuid: (message.user as { profileUserGuid?: string | null } | undefined)
            ?.profileUserGuid,
          username: message.user?.name,
          displayName: message.user?.name,
        },
      ]),
    [currentProfile, message.user],
  );

  const handleContextMenu = onLongPress
    ? (event: React.MouseEvent) => {
        event.preventDefault();
        onLongPress(message, event.currentTarget.getBoundingClientRect());
      }
    : undefined;

  const reaction = message.reaction || (message.isLiked ? '❤️' : null);
  const senderName = message.user?.name;
  const hasImage = Boolean(message.image);
  const hasText = Boolean(message.text);

  return (
    <div className={`chat-bubble-row${isSent ? ' chat-bubble-row--sent' : ' chat-bubble-row--received'}`}>
      <div className="chat-bubble-group">
        {!isSent && isGroup && senderName && (
          <span className="chat-bubble__sender-name">{senderName}</span>
        )}
        <div className={`chat-bubble-row__inner${isSent ? ' chat-bubble-row__inner--sent' : ''}`}>
          <div
            className={`chat-bubble${isSent ? ' chat-bubble--sent' : ' chat-bubble--received'}${hasImage && !hasText ? ' chat-bubble--image-only' : ''}`}
            onContextMenu={handleContextMenu}
          >
            {message.replyTo && (
              <div className="chat-bubble__reply-preview">
                <span className="chat-bubble__reply-name">
                  {message.replyTo.senderName || t('chat.unknownUser')}
                </span>
                <span className="chat-bubble__reply-text">
                  {message.replyTo.text || ''}
                </span>
              </div>
            )}

            {hasText && <p className="chat-bubble__text"><MentionText text={message.text} mentions={mentions} /></p>}

            {message.image && (
              <img
                src={message.image}
                className="chat-bubble__image"
                alt=""
                loading="lazy"
              />
            )}

            {reaction && (
              <div className="chat-bubble__reaction" aria-hidden>
                {reaction}
              </div>
            )}
          </div>
          {onLongPress && (
            <button
              type="button"
              className="chat-bubble__kebab"
              onClick={(e) => { e.stopPropagation(); onLongPress(message, e.currentTarget.getBoundingClientRect()); }}
              aria-label={t('chat.accessibility.messageActions')}
              aria-haspopup="menu"
            >
              <VerticalKebabIcon size={16} />
            </button>
          )}
        </div>

        <span className="chat-bubble__time">
          {formatTime(message.createdAt)}
          {isSent && <StatusTicks message={message} />}
        </span>
      </div>
    </div>
  );
}
